import React, { useEffect, useRef, useState } from 'react'
import Select, { SelectInstance, SingleValue } from 'react-select'
import { EventPatientDto, FindPatientsForEventDto, UserDto } from '../../sdk'
import { PatientRepository } from '../../repositories/patient.repository'
import { extractPnrCandidates, formatPnr, normalizePnr } from '../../utils/pnr'
import { buildPatientSearchQuery, MIN_NAME_SEARCH_LENGTH, MIN_PNR_SEARCH_DIGITS } from '../../utils/patientSearch'
import { mapWithConcurrency } from '../../utils/concurrency'

// How many /user-admin/{id}/simple lookups may be in flight at once while
// loading already selected patients that aren't in the preloaded list.
const PATIENT_LOOKUP_CONCURRENCY = 6

// Soft cap on how many PNRs a single paste will attempt to resolve, well
// above the "~100" the field is meant for - protects against a pathological
// paste without silently dropping normal-sized ones.
const MAX_PASTE_PNRS = 300

// How long typing has to pause before the patient search request is sent.
const SEARCH_DEBOUNCE_MS = 300

// Value of the option that adds every patient found by a multi-PNR search.
const ADD_ALL_OPTION_VALUE = '__add_all__'

type PatientOption = {
  value: string
  label: string
}

type PatientSearch = {
  // The query `results` were found for.
  query: FindPatientsForEventDto | null
  results: EventPatientDto[]
  loading: boolean
  error: string | null
}

// Progress of selecting the patients for a list of PNRs (pasted, or "Add all").
type PnrResolution =
  | { state: 'loading'; total: number }
  | { state: 'failed'; total: number; error: string }
  | {
    state: 'done'
    total: number
    // Dropped once the selection is changed another way - the counts would no
    // longer add up with the selected patients list.
    added: { count: number; alreadySelectedCount: number } | null
    notFound: string[]
    truncatedCount: number
  }

const NO_SEARCH: PatientSearch = { query: null, results: [], loading: false, error: null }

function getPatientName(patient: EventPatientDto): string {
  return `${patient.firstName} ${patient.lastName}`
}

// PNRs tell patients with the same name apart in the dropdown.
function toPatientOption(patient: EventPatientDto): PatientOption {
  const name = getPatientName(patient)
  return {
    value: patient.id,
    label: patient.personalNumber ? `${name} (${formatPnr(patient.personalNumber)})` : name,
  }
}

function mergePatients(patients: EventPatientDto[], added: EventPatientDto[]): EventPatientDto[] {
  const byId = new Map(patients.map(p => [p.id, p]))
  added.forEach(p => byId.set(p.id, p))
  return Array.from(byId.values())
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function describePnrResolution(resolution: PnrResolution): { text: string; isError: boolean } | null {
  switch (resolution.state) {
    case 'loading':
      return { text: `Looking up ${pluralize(resolution.total, 'personal number')}...`, isError: false }
    case 'failed':
      return {
        text: `Could not look up ${pluralize(resolution.total, 'personal number')}: ${resolution.error}`,
        isError: true,
      }
    case 'done': {
      const { total, added, notFound, truncatedCount } = resolution
      const sentences: string[] = []
      if (added) {
        const addedText = `${pluralize(added.count, 'patient')} added from ${pluralize(total, 'personal number')}`
        sentences.push(added.alreadySelectedCount
          ? `${addedText} (${added.alreadySelectedCount} already selected)`
          : addedText)
      }
      if (notFound.length) {
        sentences.push(`${pluralize(notFound.length, 'personal number')} not found: ${notFound.map(formatPnr).join(', ')}`)
      }
      if (truncatedCount) {
        sentences.push(`${pluralize(truncatedCount, 'more personal number')} ignored (limit ${MAX_PASTE_PNRS} per paste)`)
      }
      return sentences.length ? { text: sentences.join('. '), isError: notFound.length > 0 } : null
    }
  }
}

// Searches every patient the doctor has access to once typing pauses, and
// drops the response of a search the input has already moved on from.
function usePatientSearch(input: string): PatientSearch {
  const [search, setSearch] = useState<PatientSearch>(NO_SEARCH)

  useEffect(() => {
    const query = buildPatientSearchQuery(input)
    if (!query) {
      setSearch(NO_SEARCH)
      return
    }

    setSearch(prev => ({ ...prev, loading: true, error: null }))
    let isCancelled = false
    const timer = setTimeout(async () => {
      const res = await PatientRepository.findForEvent(query)
      if (isCancelled) return

      setSearch(res.success
        ? { query, results: res.data, loading: false, error: null }
        : { query, results: [], loading: false, error: res.error })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
  }, [input])

  return search
}

type EventPatientsFieldProps = {
  // Page 1 of the default patient query - listed while nothing is typed.
  preloadedPatients: UserDto[]
  selectedIds: string[]
  onSelectedIdsChange: React.Dispatch<React.SetStateAction<string[]>>
  // Called with true while pasted PNRs are being looked up, i.e. the selection is about to change.
  onResolvingChange: (isResolving: boolean) => void
}

// A search box that adds patients one by one or from pasted PNRs, above a
// scrollable list of the selected patients - chips inside the select would
// grow past the modal with the ~100 patients an event can have.
export const EventPatientsField = ({
  preloadedPatients,
  selectedIds,
  onSelectedIdsChange,
  onResolvingChange,
}: EventPatientsFieldProps): React.JSX.Element => {
  const [input, setInput] = useState('')
  const search = usePatientSearch(input)
  // Patients from searches, pastes and the lookup below - needed to list
  // selected patients that aren't in `preloadedPatients`.
  const [resolvedPatients, setResolvedPatients] = useState<EventPatientDto[]>([])
  const [isLoadingInitialPatients, setIsLoadingInitialPatients] = useState(false)
  const [pnrResolution, setPnrResolution] = useState<PnrResolution | null>(null)
  const [initialSelectedIds] = useState(selectedIds)
  const selectRef = useRef<SelectInstance<PatientOption>>(null)
  const isMountedRef = useRef(true)
  // For code running after an await, when this render's `selectedIds` may be out of date.
  const latestSelectedIdsRef = useRef(selectedIds)
  latestSelectedIdsRef.current = selectedIds

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Patients selected before the field opened (an edited event's patients)
  // may not be preloaded - look them up so they can be listed by name.
  useEffect(() => {
    const preloadedIds = new Set(preloadedPatients.map(p => p.id))
    const missingIds = initialSelectedIds.filter(id => !preloadedIds.has(id))
    if (missingIds.length === 0) return

    setIsLoadingInitialPatients(true)
    mapWithConcurrency(missingIds, PATIENT_LOOKUP_CONCURRENCY, async id => {
      if (!isMountedRef.current) return null
      const res = await PatientRepository.getById(id)
      return res.success ? res.data : null
    }).then(results => {
      if (!isMountedRef.current) return
      setResolvedPatients(prev => mergePatients(prev, results.filter((p): p is UserDto => p !== null)))
      setIsLoadingInitialPatients(false)
    })
  }, [initialSelectedIds, preloadedPatients])

  const hasInput = input.trim() !== ''
  const selectedIdSet = new Set(selectedIds)
  const knownPatientsById = new Map<string, EventPatientDto>(
    [...preloadedPatients, ...resolvedPatients].map(p => [p.id, p]),
  )

  // Nothing typed lists the preloaded patients, anything typed lists the search results.
  const unselectedPatients = (hasInput ? search.results : preloadedPatients).filter(p => !selectedIdSet.has(p.id))
  const canAddAllResults =
    hasInput && !search.loading && (search.query?.personalNumbers?.length ?? 0) > 1 && unselectedPatients.length > 1
  const options: PatientOption[] = [
    ...(canAddAllResults
      ? [{ value: ADD_ALL_OPTION_VALUE, label: `Add all ${unselectedPatients.length} patients found` }]
      : []),
    ...unselectedPatients.map(toPatientOption),
  ]

  const updateResolution = (resolution: PnrResolution) => {
    setPnrResolution(resolution)
    onResolvingChange(resolution.state === 'loading')
  }

  const dropAddedCounts = () => {
    setPnrResolution(prev => (prev?.state === 'done' ? { ...prev, added: null } : prev))
  }

  // Updates state with functions of the previous state, so it stays correct
  // when called after an await with values from an older render.
  const selectPatients = (patients: EventPatientDto[]) => {
    setResolvedPatients(prev => mergePatients(prev, patients))
    onSelectedIdsChange(prev => Array.from(new Set([...prev, ...patients.map(p => p.id)])))
  }

  // Selects every patient found for the given PNRs and reports which PNRs
  // matched no patient - used by pasting PNRs and by the "Add all" option.
  const selectPnrMatches = (pnrs: string[], found: EventPatientDto[], truncatedCount: number) => {
    const alreadySelectedIds = new Set(latestSelectedIdsRef.current)
    const foundPnrs = new Set(found.map(p => normalizePnr(p.personalNumber || '')))
    const addedCount = found.filter(p => !alreadySelectedIds.has(p.id)).length

    selectPatients(found)
    updateResolution({
      state: 'done',
      total: pnrs.length,
      added: { count: addedCount, alreadySelectedCount: found.length - addedCount },
      notFound: pnrs.filter(pnr => !foundPnrs.has(pnr)),
      truncatedCount,
    })
  }

  // Parses PNRs out of pasted text (any mix of the formats src/utils/pnr.ts
  // supports) and selects all their patients with a single lookup. Text
  // without PNRs is pasted into the search input as usual.
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pnrs = Array.from(new Set(extractPnrCandidates(e.clipboardData.getData('text'))))
    if (pnrs.length === 0) return

    e.preventDefault()
    if (pnrResolution?.state === 'loading') return

    selectRef.current?.blur()
    const toResolve = pnrs.slice(0, MAX_PASTE_PNRS)
    updateResolution({ state: 'loading', total: toResolve.length })

    const res = await PatientRepository.findForEvent({ personalNumbers: toResolve })
    if (!isMountedRef.current) return

    if (res.success) {
      selectPnrMatches(toResolve, res.data, pnrs.length - toResolve.length)
    } else {
      updateResolution({ state: 'failed', total: toResolve.length, error: res.error })
    }
  }

  // The select only picks patients to add - its own value stays empty.
  const handleOptionSelect = (option: SingleValue<PatientOption>) => {
    if (option?.value === ADD_ALL_OPTION_VALUE) {
      selectPnrMatches(search.query?.personalNumbers ?? [], search.results, 0)
      return
    }

    const patient = unselectedPatients.find(p => p.id === option?.value)
    if (patient) {
      selectPatients([patient])
      dropAddedCounts()
    }
  }

  const removePatient = (id: string) => {
    onSelectedIdsChange(prev => prev.filter(selectedId => selectedId !== id))
    dropAddedCounts()
  }

  const removeAllPatients = () => {
    onSelectedIdsChange([])
    dropAddedCounts()
  }

  const noOptionsMessage = (): string => {
    if (!hasInput) return 'Type a name or personal number to search'
    if (search.error) return `Search failed: ${search.error}`
    if (!buildPatientSearchQuery(input)) {
      return `Type at least ${MIN_NAME_SEARCH_LENGTH} letters of a name or ${MIN_PNR_SEARCH_DIGITS} digits of a personal number`
    }
    return search.results.length ? 'All matching patients are already selected' : 'No patients found'
  }

  const resolutionMessage = pnrResolution && describePnrResolution(pnrResolution)

  return (
    <div onPaste={handlePaste}>
      <Select
        ref={selectRef}
        options={options}
        value={null}
        inputValue={input}
        onInputChange={value => setInput(value)}
        onChange={handleOptionSelect}
        filterOption={null}
        // react-select misjudges the space below inside the scrolling modal
        // body and would shrink the menu - scroll the body to show it instead
        minMenuHeight={300}
        isLoading={search.loading}
        loadingMessage={() => 'Searching...'}
        noOptionsMessage={noOptionsMessage}
        placeholder="Search by name or personal number, or paste multiple personal numbers..."
      />
      {resolutionMessage && (
        <div className={`event-patients_status${resolutionMessage.isError ? ' event-patients_status--error' : ''}`}>
          {resolutionMessage.text}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="event-patients_selected">
          <div className="event-patients_selected-header">
            <span>{pluralize(selectedIds.length, 'patient')} selected</span>
            <button type="button" className="event-patients_remove-all-btn" onClick={removeAllPatients}>
              Remove all
            </button>
          </div>
          <ul className="event-patients_list" aria-label="Selected patients">
            {selectedIds.map(id => {
              const patient = knownPatientsById.get(id)
              const unavailableText = isLoadingInitialPatients ? 'Loading...' : 'Patient details unavailable'
              return (
                <li key={id} className="event-patients_item">
                  <span className={`event-patients_item-name${patient ? '' : ' event-patients_item-name--unavailable'}`}>
                    {patient ? getPatientName(patient) : unavailableText}
                  </span>
                  {patient?.personalNumber && (
                    <span className="event-patients_item-pnr">{formatPnr(patient.personalNumber)}</span>
                  )}
                  <button
                    type="button"
                    className="event-patients_remove-btn"
                    title="Remove"
                    aria-label={`Remove ${patient ? getPatientName(patient) : 'patient'}`}
                    onClick={() => removePatient(id)}
                  >
                    &times;
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
